import React, { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Button, Group, Title, Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { saveLocation, updateLocation, getLocationTypes } from '../../services/api.service';
import { DateTimePicker } from '@mantine/dates';

const LocationForm = ({ closeModal, isEdit, location }) => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [latitude, setLatitude] = useState(0); 
	const [longitude, setLongitude] = useState(0);
	const [locationTypes, setLocationTypes] = useState([]);
	const form = useForm({
		initialValues: {
			name: '',
			address: '',
			latlng: {
				type: 'Point',
				coordinates: [0, 0],
			},
			radius: 0,
			locationType: {
				id: 0,
			},
			startDate: null,
			endDate: null,
		},
	});

	// Change latitude
	const handleLatitudeChange = (event) => {

		// Convert to number and set in state
		const inputValue = event.currentTarget.value;
		const newLatitude = inputValue === '-' ? '-' : parseFloat(inputValue);
		setLatitude(newLatitude);
		form.setFieldValue('latlng.coordinates', [newLatitude, longitude]);

	};

	// Change longitude
	const handleLongitudeChange = (event) => {

		// Convert to number and set in state
		const inputValue = event.currentTarget.value;
		const newLongitude = inputValue === '-' ? '-' : parseFloat(inputValue);
		setLongitude(newLongitude);
		form.setFieldValue('latlng.coordinates', [latitude, newLongitude]);

	};

	// Check if edit
	useEffect(() => {

		if (isEdit) {

			// Assign form values
			form.setValues({
				name: location.name,
				address: location.address,
				latlng: location.latlng,
				startDate: location.startDate ? new Date(location.startDate) : null,
				endDate: location.endDate ? new Date(location.endDate) : null,
				locationType: location.locationType,
				radius: location.radius,
			});

			// Assign lat and long state
			setLatitude(location?.latlng?.coordinates?.[0]);
			setLongitude(location?.latlng?.coordinates?.[1]);

		}

		// eslint-disable-next-line
	}, []);

	// On load
	useEffect(() => {

		// Get location types
		getLocationTypes(function (response) {

			// Loop location types
			var locationTypes = [];
			for (var i = 0; i < response.data.length; i++) {

				// Assign
				locationTypes.push({
					label: response.data[i].name,
					value: response.data[i].id,
				});

			}

			// Set state
			setLocationTypes(locationTypes);

		}, function (error) {

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}, []);

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Conditions
		if (!isEdit) {

			// Call API
			saveLocation(values, function () {

				// Show success
				notifications.show({
					title: 'Ubicación creada correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		} else {

			// Call API
			updateLocation(location.id, values, function () {

				// Show success
				notifications.show({
					title: 'Ubicación modificada correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		}

	}

	return (
		<>
			{/* Form */}
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Title */}
				<Title
					order={5}
					mb="md"
				>
					{isEdit ? 'Modificar' : ''} Información de la Ubicación
				</Title>
				{/* Input Group */}
				<Group grow>
					{/* Name */}
					<TextInput
						required
						label="Nombre"
						placeholder="Nombre de la ubicación"
						value={form.values.name}
						onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
						{...form.getInputProps('name')}
					/>
					{/* Address */}
					<TextInput
						required
						label="Dirección"
						placeholder="Calle, número, ciudad, código postal"
						value={form.values.address}
						onChange={(event) => form.setFieldValue('address', event.currentTarget.value)}
						{...form.getInputProps('address')}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Location Type ID */}
					<Select
						searchable
						withinPortal
						required
						label="Tipo"
						placeholder="Busca un tipo"
						value={form.values.locationType.id}
						onChange={(event) => form.setFieldValue('locationType.id', event.currentTarget.value)}
						{...form.getInputProps('locationType.id')}
						nothingFound="Sin resultados"
						data={locationTypes}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Latitude */}
					<TextInput
						required
						label="Latitud"
						placeholder="0"
						value={latitude}
						onChange={handleLatitudeChange}
						// {...form.getInputProps('latlng')}
					/>
					{/* Longitude */}
					<TextInput
						required
						label="Longitud"
						placeholder="0"
						value={longitude}
						onChange={handleLongitudeChange}
						// {...form.getInputProps('latlng')}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Start Date */}
					<DateTimePicker
						required
						label="Fecha de Inicio"
						placeholder="Elige una fecha"
						valueFormat="DD-MM-YYYY HH:mm"
						value={form.values.startDate}
						onChange={(event) => form.setFieldValue('startDate', event.currentTarget.value)}
						{...form.getInputProps('startDate')}
						dropdownType="modal"
						modalProps={{
							withinPortal: true,
							centered: true
						}}
					/>
					{/* End Date */}
					<DateTimePicker
						required
						label="Fecha de Finalización"
						placeholder="Elige una fecha"
						valueFormat="DD-MM-YYYY HH:mm"
						value={form.values.endDate}
						minDate={form.values.startDate}
						onChange={(event) => form.setFieldValue('endDate', event.currentTarget.value)}
						{...form.getInputProps('endDate')}
						dropdownType="modal"
						modalProps={{
							withinPortal: true,
							centered: true
						}}
					/>
				</Group>
				{/* Buttons */}
				<Group
					position="apart"
					grow={!isEdit}
				>
					{/* Cancel */}
					<Button
						mt="xl"
						color="red"
						onClick={closeModal}
						disabled={isLoading}
						variant="light"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						Cancelar
					</Button>
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						{isEdit ? 'Guardar Cambios' : 'Crear Ubicación'}
					</Button>
				</Group>
			</form>
		</>
	);

}

export default LocationForm;
